import { EMPTY, Observable, of, pipe } from 'rxjs';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Router } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { v4 as uuidv4 } from 'uuid';

import * as CartActions from './cart.actions';
import {
    GetCart,
    PlaceOrder,
    PlaceOrderFailure,
    PlaceOrderSuccess,
    SetIsAddingAnalyticalTest,
    UpdateCart
} from './cart.actions';
import {
    InitVoucherReference,
    ResetOrderInformation,
    SetError,
    SetInvalidPage } from '../../order-checkout/store/order-checkout/order-checkout.actions';
import * as fromApp from '../app.reducers';
import {
    getCartGroups,
    getCartId,
    getCartPrice,
    getCartSamplingSupplyItems,
    getCartServiceItems,
    getLoggedInContact,
    getProfile,
    getSamplingSuppliesSampling
} from '../app.reducers';
import {
    catchError,
    concatAll,
    distinctUntilChanged,
    map,
    mergeMap,
    skipWhile,
    switchMap,
    tap,
    withLatestFrom
} from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import {
    Cart,
    CART_ITEM_TYPE_PRODUCT,
    CART_ITEM_TYPE_SERVICE,
    CartGroup,
    CartItem,
    CartSampling,
    PlaceOrderRequest
} from '../../model/cart.model';
import { HeadersEnum, ParamsKeys, ParamsValues, RestURLsEnum } from '../../model/http-request-enums';
import { SamplingItemNode } from '../../model/sampling-item/sampling-item.model';
import { OrderStatus } from '../../constants/order-status';

import { getPageToComplete } from '../../order-checkout/guards/checkout.guard';
import { ToggleSampleTypeSelectorPopup } from '../layout/layout.actions';
import { GetLoggedInContact } from '../labsys-contact/labsys-contact.actions';
import { OrderInformation } from '../../model/order-information.model';
import { cartHasServiceItemsOnly } from '../../order-checkout/shared/cart';
import { Angulartics2 } from 'angulartics2';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { Price } from '@app/model/catalogue-category/price.model';
import { GoogleAnalyticsService } from '@app/services/google-analytics.service';
import { CartService } from '@app/services/cart.service';
import { Profile } from '@app/services/profile.model';
import { getCartGroupType, voucherErrorReference } from '@app/store/cart/cart.reducers.utils';
import { PromoCodeService, PromoCodeValidationResult } from '@app/cart/services/promo-code/promo-code.service';

type AddCartItemArg = [CartActions.AddCartItem | CartActions.UpdateCartItem, string];

const transformVoucherErrorToLimbusError = (response: HttpErrorResponse) => {
    if (response.status === 400 && response.error && response.error['OrderHeader.VoucherReference']) {
        return {
            ...response,
            error: { messages: [
                {
                    'resourceType': 'reference-with-message.v1',
                    'reference': voucherErrorReference,
                    'message': response.error['OrderHeader.VoucherReference'][0]
                }
            ]
            }
        };
    }

    return response;

};

interface ValidateCodePipelineType {
    action: PlaceOrder | PlaceOrderFailure;
    result: PromoCodeValidationResult;
}

@Injectable()
export class CartEffects {

    csCartUrl = environment.csBaseURL + RestURLsEnum.CART_BASE;
    header = new HttpHeaders({
        'Accept': [
            HeadersEnum.ACCEPT_CART,
            HeadersEnum.ACCEPT_ERROR,
        ]
    });

    constructor(private actions$: Actions,
        public httpClient: HttpClient,
        public store: Store<fromApp.AppState>,
        public router: Router,
        private notification: NzNotificationService,
        private angulartics2: Angulartics2,
        private googleAnalytics: GoogleAnalyticsService,
        private cart: CartService,
        private promoCodeService: PromoCodeService
    ) {
    }


    getCart = createEffect(() => this.actions$.pipe(
        ofType(CartActions.GET_CART),
        switchMap((action: CartActions.GetCart) => this.cart.get$(action.cartId).pipe(
            map((cart) => [new CartActions.UpdateCart(cart), new CartActions.GetCartSucces()]),
            catchError(error => {
                const [title, content] = getCartGrowlError;
                this.notification.error(title, content);
                return [[new CartActions.GetCartFail()]];
            })
        )),
        concatAll()
    ));


    reloadCart$ = createEffect(() => this.actions$.pipe(
        ofType(CartActions.RELOAD_CART),
        withLatestFrom(getCartId),
        switchMap((id) => [new GetCart(id)])
    ));


    updateAutoPopulatedCartItem = createEffect(() => this.actions$.pipe(
        ofType(CartActions.UPDATE_AUTOPOPULATED_CART_ITEM),
        withLatestFrom(this.store.select(getCartId)),
        mergeMap(getGroupSuggestionIfNeeded(this)),
        mergeMap(getGroupIfNeed(this)),
        withLatestFrom(...selectRequiredStates(this)),
        mergeMap(doPatch(this, true)),
        tap(doSubstanceEffects(this)),
        map(handleUpdateFail(this))
    ));


    addCartItem = createEffect(() => this.actions$.pipe(
        ofType(CartActions.ADD_CART_ITEM, CartActions.UPDATE_CART_ITEM),
        withLatestFrom(this.store.select(getCartId)),
        mergeMap(getGroupSuggestionIfNeeded(this)),
        mergeMap(getGroupIfNeed(this)),
        withLatestFrom(...selectRequiredStates(this)),
        mergeMap(doPatch(this, false)),
        tap(doSubstanceEffects(this)),
        map(handleUpdateFail(this))
    ));


    deleteCartItems = createEffect(() => this.actions$.pipe(
        ofType(CartActions.DELETE_CART_ITEMS),
        withLatestFrom(this.store.select(getCartId)),
        mergeMap(getGroupSuggestionIfNeeded(this)),
        mergeMap(getGroupIfNeed(this)),
        withLatestFrom(...selectRequiredStates(this)),
        mergeMap(performDeleteItems(this)),
        tap(doSubstanceEffects(this)),
        map(handleUpdateFail(this))
    ));


    updateSamplingName = createEffect(() => this.actions$.pipe(
        ofType(CartActions.UPDATE_SAMPLING_NAME),
        withLatestFrom(this.store.select(getCartId)),
        switchMap(([{ groupId, groupNewName }, cartId]: [CartActions.UpdateSampleName, string]) =>
            this.cart.patchGroupName$(cartId, groupId, groupNewName).pipe(
                map((updatedCart) => (
                    { type: CartActions.UPDATE_CART, payload: updatedCart })),
                catchError((err) => [new CartActions.SetGroupErrorMessage(err, groupId, groupNewName)])))
    ));


    deleteCartGroup = createEffect(() => this.actions$.pipe(
        ofType(CartActions.DELETE_CART_GROUP),
        withLatestFrom(this.store.select(getCartId)),
        switchMap(([{ groupId }, cartId]: [CartActions.DeleteCartGroup, string]) => this.doDeleteGroupRequest(cartId, groupId).pipe(
            map((deletedCart) => (
                { type: CartActions.UPDATE_CART, payload: deletedCart })),
            catchError((err) => [new CartActions.SetGroupErrorMessage(err, groupId, null)])))
    ));

    validatePromoCodePipeline = pipe(
        switchMap((action: PlaceOrder): Observable<ValidateCodePipelineType> => {

            const voucherReference = action.order.voucherReference;

            if (!voucherReference) { // don't validate, just place the order
                return of({ action, result: { valid: true, message: '' } });
            }

            return this.promoCodeService
                .placeOrderValidate(action.order.voucherReference)
                .pipe(
                    map((result: PromoCodeValidationResult) => {

                        if (!result.valid) {
                            const error = new HttpErrorResponse(
                                {
                                    status: 400,
                                    error: { messages: [
                                        {
                                            'resourceType': 'reference-with-message.v1',
                                            'reference': voucherErrorReference,
                                            'message': result.message
                                        }
                                    ]
                                    }
                                }
                            );

                            return { action: new PlaceOrderFailure(error), result };
                        }

                        // pass through action
                        return { action, result };
                    })
                );
        })
    );

    placeOrderPipeline = pipe(
        withLatestFrom(
            this.store.pipe(select(getCartId)),
            this.store.pipe(select(getCartPrice)),
            this.store.pipe(select(getCartGroups)),
            this.store.pipe(select(getProfile)),
            this.store.pipe(select(getSamplingSuppliesSampling))
        ),
        tap(_ => {
            this.store.dispatch(new CartActions.SetOrderStatus(OrderStatus.IN_PROGRESS));
        }),
        switchMap(
            ([
                action,
                cartId,
                cartPrice,
                groups,
                profile,
                samplingSupplySampling
            ]: [
                PlaceOrder,
                string,
                Price,
                CartGroup[],
                Profile,
                CartSampling
            ]) => {
                const orderHeader = cleanOrderReferences(action.order);
                const samplingSupplyGroupId = samplingSupplySampling.groupId;

                const payload: PlaceOrderRequest = {
                    orderHeader,
                    samplingSupplyGroupId,
                    autoPopulatedItems: []
                };

                const requestFn$ = (id, data) => this.cart.placeOrder$(id, data);

                return requestFn$(cartId, payload).pipe(
                    tap(_ => {
                        this.googleAnalytics.trackTransaction(
                            uuidv4(),
                            profile.email,
                            cartPrice.amount,
                            groups
                        );
                    }),
                    mergeMap(updatedCart => [
                        new PlaceOrderSuccess(updatedCart),
                        new ResetOrderInformation()
                    ]),
                    catchError(err =>
                        of(
                            new CartActions.PlaceOrderFailure(
                                transformVoucherErrorToLimbusError(err)
                            )
                        )
                    )
                );
            }
        )
    );



    placeOrder = createEffect(() => this.actions$.pipe(
        ofType(CartActions.PLACE_ORDER),
        this.validatePromoCodePipeline,
        switchMap(({ action, result }) => result.valid ? of(action).pipe(this.placeOrderPipeline) : of(action))
    ));


    LoadNewCartOnPlaceOrderSuccess = createEffect(() => this.actions$.pipe(
        ofType(CartActions.PLACE_ORDER_SUCCESS),
        switchMap((action: PlaceOrderSuccess) => this.cart.loadNewCart().pipe(map(cart => new UpdateCart(cart))))
    ));


    PlaceOrderSuccess = createEffect(() => this.actions$.pipe(
        ofType(CartActions.PLACE_ORDER_SUCCESS),
        tap((action: PlaceOrderSuccess) => {
            this.angulartics2.eventTrack.next({
                action: 'OrderPlaced',
                properties: { category: 'Cart', label: 'Order Placed' },
            });
        })
    ), { dispatch: false });

    // reload profile once after placing an order to handle newly created accounts

    reloadProfileAfterFirstOrder = createEffect(() => this.actions$.pipe(
        ofType(CartActions.PLACE_ORDER_SUCCESS),
        withLatestFrom(
            this.store.select(getProfile),
            this.store.select(getLoggedInContact)),
        skipWhile(([action, user, contact]: any) => contact !== null),
        switchMap(([action, user, contact]: any) => {
            const id = user.sub;
            if (!id) {
                return EMPTY;
            }
            return [new GetLoggedInContact(id)];
        })
    ));


    clearSampleTypeSelector = createEffect(() => this.actions$.pipe(
        ofType(CartActions.UPDATE_CART, CartActions.UPDATE_CART_FAIL),
        switchMap((action: any) => [new CartActions.SetIsAddingAnalyticalTest(false)])
    ));


    handlePlaceOrderError = createEffect(() => this.actions$.pipe(
        ofType(CartActions.PLACE_ORDER_FAILURE),
        withLatestFrom(
            this.store.select(getCartSamplingSupplyItems),
            this.store.select(getCartServiceItems)
        ),
        tap(([action, supplies, services]: [PlaceOrderFailure, CartItem[], CartItem[]]) => {

            const pageToValidate = getPageToComplete(action.payload.error, cartHasServiceItemsOnly(supplies, services));
            this.store.dispatch(new SetInvalidPage(pageToValidate));
        }),
        switchMap(_ => [new SetError(['Please complete your details.'])])
    ));

    // init voucher reference after cart is loaded
    initVoucherReferenceOnGetCart$ = createEffect(() => this.actions$.pipe(
        ofType(CartActions.UPDATE_CART),
        distinctUntilChanged((
            { payload: p1 }: UpdateCart,
            { payload: p2 }: UpdateCart) => p1.id === p2.id),
        map((action: UpdateCart) => {
            const { payload: { id } } = action;
            return new InitVoucherReference(id);
        })
    ),
    );

    // @ts-ignore
    getNewGroup(cartId, groupName, cartItem) {
        const itemType = getCartGroupType(cartItem.resourceType);
        const prefix = (itemType === CART_ITEM_TYPE_PRODUCT) ? 'product' : 'service';
        const addGroupUrl = this.csCartUrl + cartId.toString() + `/${prefix}-group`;

        const params: HttpParams = new HttpParams()
            .set(ParamsKeys.INCLUDE,
                [ParamsValues.CATALOGUE_ITEMS, ParamsValues.PRICES].join(','))
            .set('name', groupName)
            .set(ParamsKeys.SORT_BY,
                [ParamsValues.CREATION_INSTANT_DESC, ParamsValues.CREATION_GRP_INSTANT_DESC].join(','));


        return this.httpClient.post<Cart>(addGroupUrl, '', {
            params: params,
            headers: this.header,
            observe: 'response'
        }).pipe(
            map(resp => {
                const newGroupId = +resp.body.groupId - 1;

                return { cartItem, groupId: newGroupId, cartId, groupName };
            }));
    }

    // @ts-ignore
    getProductGroupSuggestion(catalogueId, cartId) {
        return this.cart.newProductGroupSuggestion$(cartId, catalogueId)
            .pipe(map(({ suggestions }: SamplingItemNode) => ({
                items: suggestions.map(({ groupId, groupName: name }) => new CartSampling(groupId, name))
            })));
    }

    // Return true if cart group has only one item and if the item matches input cartItem
    isGroupToBeDeleted(cartGroups: CartGroup[], groupId: number, cartItems: CartItem[]): boolean {
        const cartGroup = cartGroups.find(group => group.id === groupId);

        const remainingUserAddedItems = cartGroup.items.filter(i => i.userAdded);

        const remainingCartItemIds = new Set(remainingUserAddedItems.map(ci => +ci.item.id));
        const deletionCandidateItemIds = new Set(cartItems.map(ci => +ci.item.id));

        if (remainingCartItemIds.size !== deletionCandidateItemIds.size) {
            return false;
        }

        for (const id of remainingCartItemIds) {
            if (!deletionCandidateItemIds.has(id)) {
                return false;
            }
        }

        return true;
    }

    doDeleteGroupRequest(cartId: string, groupId: number): Observable<Cart> {
        return this.cart.removeGroup$(cartId, groupId);
    }
}

const getCartGrowlError = [
    'Failed to get cart',
    'Something went wrong.'
];

// Begin patch cart functions

const getGroupSuggestionIfNeeded = (self: CartEffects) => ([{ payload: cartItem, sampling }, cartId]: AddCartItemArg) => {
    // for service(analytical test) items, groupName from sampling is already set in ui

    const itemType = getCartGroupType(cartItem.resourceType);
    if (itemType === CART_ITEM_TYPE_SERVICE) {
        return [{ cartId, cartItem, sampling }];
    }

    if (sampling !== null) {
        return [{ cartId, cartItem, sampling }];
    }
    // request for product group suggestion first to get the groupName
    return self.getProductGroupSuggestion(cartItem.item.id, cartId)
        .pipe(map(({ items }) => {
            const samplingItem = items[0];
            return { cartId, cartItem, sampling: samplingItem };
        }));

};

// @ts-ignore
const getGroupIfNeed = (self: CartEffects) => ({ cartId, cartItem, sampling }) => {
    const { name: groupName, groupId } = sampling;

    if (groupId !== null) {
        return [{ cartItem, groupId, cartId, groupName }];
    }
    // for groupId === null request of a new group
    return self.getNewGroup(cartId, groupName, cartItem);
};

// @ts-ignore
const doSubstanceEffects = (self: CartEffects) => _ => {
    // this.store.dispatch(new SubstanceActions.ResetSelectedSubstanceAttributeValueIds());
    self.store.dispatch(new ToggleSampleTypeSelectorPopup(false));
    self.store.dispatch(new SetIsAddingAnalyticalTest(false));
};

// @ts-ignore
const handleUpdateFail = (self: CartEffects) => (response) => {
    if (response instanceof CartActions.UpdateCartFail) {
        const { error, cartId } = response;
        self.store.dispatch(new GetCart(cartId));
        return response;
    }

    const { response: payload, item, groupId } = response;
    return new UpdateCart(payload, !!item ? item.id : undefined, groupId);
};

// @ts-ignore
const doPatch = (self: CartEffects, autoPopulated) => (param): Observable<any> => {
    const [{ cartItem, groupId, cartId, groupName }, cartGroups] = param;
    let params: HttpParams = new HttpParams(cartItem)
        .set(ParamsKeys.INCLUDE,
            [ParamsValues.CATALOGUE_ITEMS, ParamsValues.PRICES].join(','))
        .set(ParamsKeys.EXCLUDE, ParamsValues.NO_PRICE)
        .set(ParamsKeys.SORT_BY,
            [
                ParamsValues.CART_GROUP_TYPE,
                ParamsValues.CART_GROUP_CREATION_INSTANT,
                ParamsValues.CREATION_INSTANT_DESC,
                ParamsValues.CREATION_GRP_INSTANT_DESC
            ].join(',')
        );

    const itemType = getCartGroupType(cartItem.resourceType);
    /* todo: 1. Ensure typing of Cart, CartGroup, CartItems and corresponding item - CatalogueItem | AnalyticalTest
  *  2. check type using instanceOf [Type]
  * */

    if (autoPopulated) {
        params = params.set(ParamsKeys.CART_AUTOPOPULATED_QUANTITY, `${cartItem.autoPopulatedQty}`);
    } else {
        params = params.set(ParamsKeys.CART_QUANTITY, `${cartItem.quantity}`);
    }

    const prefix = (itemType === CART_ITEM_TYPE_PRODUCT) ? 'product' : 'service';
    const updateCartItemUrl = self.csCartUrl + cartId + `/${prefix}-group/` + groupId + '/item/' + cartItem.item.id;
    const deleteCartItemUrl = self.csCartUrl + cartId + '/group/' + groupId + '/item/' + cartItem.item.id;

    const normalizedAutoPopulatedQty = !cartItem.autoPopulatedQty ? 0 : +cartItem.autoPopulatedQty;

    const isUpdateAction = +cartItem.quantity !== 0 || normalizedAutoPopulatedQty !== 0;

    const url = isUpdateAction ? updateCartItemUrl : deleteCartItemUrl;

    let request: Observable<any>;

    if (isUpdateAction) {
        request = self.httpClient
            .patch<Cart>(url, '', { params: params, headers: self.header }).pipe(
            map(data => ({ response: data, item: cartItem.item }))
        );
    } else if (self.isGroupToBeDeleted(cartGroups, groupId, [cartItem])) {
        request = self.doDeleteGroupRequest(cartId, groupId).pipe(
            map(data => ({ response: data, item: cartItem.item, groupId })),
            tap(responseObject => {
                if (responseObject.response.groups.filter(x => x.unavailableItems && x.unavailableItems.length > 0).length > 0) {
                    // Workaround for CP-1031:
                    // Tests that have catalogue items with more restrictive permissions than "EVERYONE" (i.e. "USERS" and above)
                    // were not able to be added to the cart. This has been fixed in CP-1031, but an additional bug was found
                    // where, if these bugged tests are in the cart under at least one sample (say Sample A), and you have at least
                    // one test under another sample (say Sample B), then deleting all tests under Sample B would cause a UI
                    // bug that "deletes" the entire cart, including all tests under Sample A.
                    // Refreshing the page returns the cart back to normal. Since the Java cart is meant to be rewritten soon,
                    // we implemented the below reload workaround as a quick fix rather than investigating the Java cart further.
                    window.location.reload();
                }
            }));
    } else {
        request = self.httpClient.delete<Cart>(url, { params: params, headers: self.header }).pipe(
            map(data => ({ response: data, item: cartItem.item, groupId })));
    }
    return request.pipe(
        catchError(_ => [
            new CartActions.UpdateCartFail(
                `Something went wrong when ${
                    isUpdateAction ? 'adding' : 'deleting'
                } ${cartItem.item.name} for ${groupName} ${
                    isUpdateAction ? 'to' : 'from'
                } your cart. ` +
              `Please either ${
                  isUpdateAction ? 'remove this item' : 'try again'
              } or contact us for assistance.`,
                cartId,
                groupId,
                cartItem.item.id
            )
        ]));
};

const performDeleteItems = (self: CartEffects) => (param): Observable<any> => {
    const [{ cartItem, groupId, cartId, groupName },
        selectedOptions, sampleTypeAttribute, selectedSubstance, cartGroups] = param;

    const cartItems = cartItem.map(item => item.clone());
    const cartItemIds = cartItems.map(item => item.item.id);
    const queryParams = cartItemIds.join('&catalogueItemIds=');
    cartItems.forEach(item => item.quantity = 0);

    const url = self.csCartUrl + cartId + '/group/' + groupId + '/item?catalogueItemIds=' + queryParams;

    let request: Observable<any>;
    if (self.isGroupToBeDeleted(cartGroups, groupId, cartItems)) {
        request = self.doDeleteGroupRequest(cartId, groupId).pipe(
            map(data => ({ response: data, items: cartItems.map(c => c.item) })),
            tap(responseObject => {
                if (responseObject.response.groups.filter(x => x.unavailableItems && x.unavailableItems.length > 0).length > 0) {
                    window.location.reload();
                }
            }));
    } else {
        request = self.httpClient.delete<Cart>(url, { headers: self.header }).pipe(
            map(data => ({ response: data })));
    }

    return request
        .pipe(
            catchError(_ => [
                new CartActions.UpdateCartFail(
                    `Something went wrong when deleting the items for ${groupName} from your cart.` +
                    ' Please either try again or contact us for assistance.', cartId)]));
};

const selectRequiredStates = (self: CartEffects) => [
    self.store.select(fromApp.getCartGroups)];

// End patch cart functions

// remove empty order references
const cleanOrderReferences = (order: OrderInformation) => {

    const fields = [
        'customerOrderNumber',
        'customerReference',
        'customerOtherReference'
    ];

    const result = fields.reduce((s, t) => {
        const value = order[t];

        if (!value || value.trim() === '') {
            const clone = { ...s };
            delete clone[t];
            return clone;
        }

        return s;

    }, { ...order });

    return result;
};
